import React, { useState } from 'react';
import { Flex } from 'antd';
import { FolderOutlined } from '@ant-design/icons';
import Button from 'admin/components/common/Button';
import { TextField } from 'admin/components/common/Form/TextField';
import { TextAreaPure } from 'admin/components/common/Form/TextArea';
import { Modal, ModalBody, ModalFooter } from 'admin/components/common/Modal';
import Text from 'admin/components/common/Text';
import type { DesignPreset } from 'common/design-tokens';
import { getDesignPresetNameParts } from 'common/design-tokens/preset-paths';
import t from 'utils/translate';

type PresetDetails = Pick<DesignPreset, 'name' | 'description'>;

export default function DesignPresetDetailsModal({
	initialValue,
	theme,
	disabled,
	onSave,
	onClose,
}: {
	initialValue?: PresetDetails;
	theme: 'light' | 'dark';
	disabled?: boolean;
	onSave: (details: PresetDetails) => void;
	onClose: () => void;
}) {
	const [details, setDetails] = useState<PresetDetails>(initialValue ?? { name: '', description: '' });
	const parts = getDesignPresetNameParts(details.name);
	const folder = parts.slice(0, -1).join(' / ');
	const creating = !initialValue;
	const mutedColor = theme === 'dark' ? Text.color.whiteMid : Text.color.blackMid;

	return (
		<Modal
			open
			isNested
			theme={theme}
			maskColor="dark"
			width={564}
			onCancel={onClose}
			title={
				<Text tag="span" size={Text.size.subtitle} weight={Text.weight.semibold} compact>
					{t(creating ? 'presets.add' : 'presets.rename')}
				</Text>
			}
		>
			<form
				onSubmit={event => {
					event.preventDefault();
					// Modal portals still bubble React events to the host Story settings form.
					event.stopPropagation();
					if (disabled || !parts.length) return;
					onSave({ name: details.name.trim(), description: details.description?.trim() || undefined });
				}}
			>
				<ModalBody>
					<Flex vertical gap={24}>
						<Text size={Text.size.body} color={mutedColor} compact>
							{creating
								? 'Create a reusable style for your elements. Then add properties such as typography, colors or spacing.'
								: 'Update the name and description. Elements linked to this preset keep their styles.'}
						</Text>
						<Flex vertical gap={12}>
							<TextField
								id="design-preset-name"
								theme={theme}
								autoFocus
								isRequired
								isLabelUppercase={false}
								label={t('presets.name')}
								placeholder="For example, Buttons/Primary"
								value={details.name}
								disabled={disabled}
								onChange={event => setDetails(current => ({ ...current, name: event.target.value }))}
							/>
							<Text size={Text.size.body} color={mutedColor} compact>
								Use / to organize presets into folders. Buttons/Primary creates a Primary preset inside
								the Buttons folder. A name without / stays at the top level.
							</Text>
							{parts.length > 0 && (
								<Flex align="center" gap={8} wrap>
									{folder && <FolderOutlined aria-hidden="true" />}
									<Text tag="span" size={Text.size.body} color={mutedColor} compact>
										{folder || 'Top level'}
									</Text>
									<span aria-hidden="true">/</span>
									<Text tag="span" size={Text.size.body} weight={Text.weight.semibold} compact>
										{parts.at(-1)}
									</Text>
								</Flex>
							)}
						</Flex>
						<TextAreaPure
							theme={theme}
							label={{ children: t('presets.description') }}
							aria-label={t('presets.description')}
							placeholder="When should this preset be used?"
							autoSize={{ minRows: 2, maxRows: 4 }}
							value={details.description ?? ''}
							disabled={disabled}
							onChange={event => setDetails(current => ({ ...current, description: event.target.value }))}
						/>
						<Text size={Text.size.body} color={mutedColor} compact>
							Rename or move presets later from their actions menu.
						</Text>
					</Flex>
				</ModalBody>
				<ModalFooter>
					<Button type="submit" theme={theme} disabled={disabled || !parts.length}>
						{t(creating ? 'presets.add' : 'designTokens.save')}
					</Button>
					<Button theme={theme} view="secondary-gray" onClick={onClose}>
						{t('brandKit.cancel')}
					</Button>
				</ModalFooter>
			</form>
		</Modal>
	);
}
